import { Box, Modal } from "@mui/material";
import {
  ContentBlock,
  convertToRaw,
  EditorState,
  RichUtils,
  Modifier,
  AtomicBlockUtils,
  ContentState,
  SelectionState,
  convertFromHTML,
  genKey,
  CharacterMetadata,
} from "draft-js";
import type { DraftHandleValue } from "draft-js";
import { List } from "immutable";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "draft-js/dist/Draft.css";
import PreviewLongPost from "./PreviewLongPost";
import draftToHtml from "draftjs-to-html";
import {
  useCreateLongPostMutation,
  useUpdatePostMutation,
  useUploadImageLongPostMutation,
} from "@/store/api/postApi";
import { useValidateSymbolMutation } from "@/store/api/financeApi";
import { decorator, fontSizeOptions, fontSizeStyles, safeAppend } from "./utilsLongPost";
import { TICKER_REGEX } from "@/components/UI/modal/CreatePostModal/component/tickerSchema";
import { useGetUniversalSearchQuery } from "@/store/api/userApi";
import LongPostHeader from "./LongPostHeader";
import PostEditorForm from "./PostEditorForm";
import EditorToolbar from "./EditorToolbar";
import { setDraftLoading, setEditingPostLoading, setPostLoading } from "@/store/slice/loadingSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { resetPage } from "@/store/slice/postParamsSlice";
import PreviewHeader from "./PreviewHeader";
import ArticleInfoPanel from "./ArticleInfoPanel";

const LINE_VISUAL_OFFSET = 4;
const LINE_CONTROL_MARGIN = 32;

const CreateLongPostModal: FC<any> = ({
  open,
  editPost,
  setToast,
  setIsLongPostModalOpen,
  isPreview = false,
  isDraft = false,
}) => {
  const [createLongPost] = useCreateLongPostMutation();
  const [validateSymbol] = useValidateSymbolMutation();
  const [updatePost] = useUpdatePostMutation();
  const [uploadInlineImage] = useUploadImageLongPostMutation();
  const [preview, setPreview] = useState(false);
  const [price, setPrice] = useState("");
  const [invalidPrice, setInvalidPrice] = useState(false);
  const [slider, setSlider] = useState(0);
  const [invalidCashtags, setInvalidCashtags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [selectedSize, setSelectedSize] = useState("15");
  const [allowAnyAmount, setAllowAnyAmount] = useState(false);
  const [title, setTitle] = useState("");
  const [isPaidContent, setIsPaidContent] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [togglePaidContent, setTogglePaidContent] = useState(false);
  const [chipSelect, setChipSelect] = useState<string[]>([]);
  const dispatch = useDispatch();
  const [mentionStartPos, setMentionStartPos] = useState<null | { top: number; left: number }>(
    null
  );
  const [freeCharCount, setFreeCharCount] = useState(0);
  const imageBlurPercentRef = useRef(0);
  const [lineY, setLineY] = useState(0);
  const lineRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeField, setActiveField] = useState<"title" | "summary" | "body">("body");
  const bodyWasFocusedRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [lineYReady, setLineYReady] = useState(!editPost?.freeContentRange);

  const [replacementFiles, setReplacementFiles] = useState<File[]>([]);
  const [imageReplacements, setImageReplacements] = useState<string[]>([]);
  const [removedImageUrls, setRemovedImageUrls] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const editSnapshotRef = useRef<{
    title: string;
    summary: string;
    body: string;
    image: string | null;
    chips: string[];
    slider: number;
    isPaid: boolean;
    price: string;
    allowAny: boolean;
    freeCharCount: number;
  } | null>(null);

  const [editorStateTitle, setEditorStateTitle] = useState(() =>
    EditorState.createEmpty(decorator)
  );
  const [editorStateSummary, setEditorStateSummary] = useState(() =>
    EditorState.createEmpty(decorator)
  );
  const [editorStateBody, setEditorStateBody] = useState(() => EditorState.createEmpty(decorator));
  const editorStateBodyRef = useRef(editorStateBody);
  editorStateBodyRef.current = editorStateBody;

  const summaryEditorRef = useRef<HTMLDivElement | null>(null);
  const bodyEditorRef = useRef<HTMLDivElement | null>(null);

  const currentEditorState =
    activeField === "title"
      ? editorStateTitle
      : activeField === "summary"
        ? editorStateSummary
        : editorStateBody;

  const setCurrentEditorState =
    activeField === "title"
      ? setEditorStateTitle
      : activeField === "summary"
        ? setEditorStateSummary
        : setEditorStateBody;

  const [isPublished, setIsPublished] = useState(false);
  const [showPublishRequirements, setShowPublishRequirements] = useState(true);

  const handleClose = async () => {
    setIsLongPostModalOpen(false);
    Clear();
  };

  const closeAndDraft = () => {
    if (editPost && editPost.isPublished) {
      handleClose();
      return;
    }

    handleClose();

    if (!isPreview) {
      handlePublish(isPublished).catch((err) => {
        console.error("Failed to save draft", err);
      });
    }
  };

  useEffect(() => {
    if (editPost && isPaidContent) {
      const num = Number(price);
      setInvalidPrice(!price || !Number.isFinite(num) || num <= 0);
    } else {
      setInvalidPrice(isPaidContent);
    }
  }, [isPaidContent]);

  useEffect(() => {
    if (!isPaidContent) return;
    if (editPost?.freeContentRange) return;

    const scrollEl = scrollContainerRef.current;
    const editorEl = editorContainerRef.current;
    if (!scrollEl || !editorEl) return;

    const editorRect = editorEl.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const visibleTop = Math.max(scrollRect.top, editorRect.top);
    const visibleBottom = Math.min(scrollRect.bottom, editorRect.bottom);
    const centerLineY = Math.max(0, (visibleTop + visibleBottom) / 2 - editorRect.top);
    let newLineY = centerLineY;

    const selection = editorStateBodyRef.current.getSelection();
    const anchorKey = selection.getAnchorKey();
    const anchorOffset = selection.getAnchorOffset();

    if (bodyWasFocusedRef.current && anchorKey) {
      const blockEl = editorEl.querySelector(`[data-offset-key^="${anchorKey}-"]`) as HTMLElement;
      if (blockEl) {
        let placed = false;
        const textNodes = getTextNodesInElement(blockEl);
        let remaining = anchorOffset;
        for (const textNode of textNodes) {
          const len = textNode.textContent?.length ?? 0;
          if (remaining <= len) {
            try {
              const range = document.createRange();
              range.setStart(textNode, remaining);
              range.setEnd(textNode, remaining);
              const rects = range.getClientRects();
              if (rects.length > 0) {
                newLineY = Math.max(0, rects[0].bottom - editorRect.top);
                placed = true;
              }
            } catch {}
            break;
          }
          remaining -= len;
        }
        if (!placed) {
          const blockRect = blockEl.getBoundingClientRect();
          newLineY = Math.max(0, blockRect.bottom - editorRect.top);
        }
      }
    }

    const lineViewportY = editorRect.top + newLineY;
    if (
      lineViewportY - LINE_CONTROL_MARGIN < visibleTop ||
      lineViewportY + LINE_CONTROL_MARGIN > visibleBottom
    ) {
      newLineY = centerLineY;
    }

    setLineY(newLineY);
    const freeChars = convertLineYToFreeCharCountOptimized(newLineY + LINE_VISUAL_OFFSET);
    setFreeCharCount(freeChars);
  }, [isPaidContent]);

  const getTextNodesInElement = (element: HTMLElement): Text[] => {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    return textNodes;
  };

  const convertLineYToFreeCharCountOptimized = (currentLineY: number): number => {
    const container = editorContainerRef.current;
    if (!container) return 0;

    const containerRect = container.getBoundingClientRect();
    const contentState = editorStateBodyRef.current.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    let totalFreeChars = 0;
    imageBlurPercentRef.current = 0;

    for (const block of blocks) {
      const key = block.getKey();
      const blockText = block.getText();
      const isAtomicBlock = block.getType() === "atomic";
      const blockCharCount = isAtomicBlock ? 1 : blockText.trim() ? blockText.trimEnd().length : 0;

      const domNode = container.querySelector(`[data-offset-key^="${key}-"]`) as HTMLElement;

      if (!domNode) {
        totalFreeChars += blockCharCount;
        continue;
      }

      const nodeRect = domNode.getBoundingClientRect();
      const nodeTop = nodeRect.top - containerRect.top;
      const nodeBottom = nodeTop + nodeRect.height;

      if (nodeBottom <= currentLineY) {
        totalFreeChars += blockCharCount;
        continue;
      }

      if (nodeTop >= currentLineY) {
        break;
      }

      if (isAtomicBlock) {
        const imgEl = domNode.querySelector("img");
        if (imgEl) {
          const imgRect = imgEl.getBoundingClientRect();
          const imgTop = imgRect.top - containerRect.top;
          const imgBottom = imgTop + imgRect.height;

          if (currentLineY <= imgTop) {
            // Line is in margin above the image — image is blurred
            imageBlurPercentRef.current = 0;
          } else if (currentLineY >= imgBottom) {
            // Line is in margin below the image — image is fully free
            imageBlurPercentRef.current = 0;
            totalFreeChars += 1;
          } else {
            // Line crosses the actual image
            const ratio = (currentLineY - imgTop) / imgRect.height;
            imageBlurPercentRef.current = Math.max(0.01, Math.min(0.99, ratio));
            totalFreeChars += 1;
          }
        } else {
          totalFreeChars += 1;
        }
      } else {
        const charsInBlock = findCharsBeforeLineInBlock(domNode, currentLineY, containerRect);
        totalFreeChars += Math.min(charsInBlock, blockCharCount);
      }
      break;
    }

    return totalFreeChars;
  };

  const findCharsBeforeLineInBlock = (
    domNode: HTMLElement,
    lineY: number,
    containerRect: DOMRect
  ): number => {
    if (!domNode.textContent || !domNode.textContent.trim()) return 0;

    const textNodes = getTextNodesInElement(domNode);

    if (textNodes.length === 0) return 0;

    interface TextNodeInfo {
      node: Text;
      startOffset: number;
      length: number;
    }

    const nodeInfos: TextNodeInfo[] = [];
    let totalLength = 0;

    for (const node of textNodes) {
      const length = node.textContent?.length || 0;
      nodeInfos.push({ node, startOffset: totalLength, length });
      totalLength += length;
    }

    if (totalLength === 0) return 0;

    const getCharBottom = (globalIndex: number): number => {
      for (const info of nodeInfos) {
        if (globalIndex < info.startOffset + info.length) {
          const localIndex = globalIndex - info.startOffset;
          const range = document.createRange();
          range.setStart(info.node, localIndex);
          range.setEnd(info.node, Math.min(localIndex + 1, info.length));
          const rect = range.getBoundingClientRect();
          return rect.bottom - containerRect.top;
        }
      }
      return Infinity;
    };

    let low = 0;
    let high = totalLength;
    let result = 0;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      const charBottom = getCharBottom(mid);

      if (charBottom <= lineY) {
        result = mid + 1;
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return result;
  };

  const convertFreeCharCountToLineY = (freeChars: number): number => {
    const container = editorContainerRef.current;
    if (!container) return 0;

    const containerRect = container.getBoundingClientRect();
    const contentState = editorStateBodyRef.current.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    let charCount = 0;

    for (const block of blocks) {
      const blockText = block.getText();
      const key = block.getKey();
      const isAtomicBlock = block.getType() === "atomic";
      const blockCharCount = isAtomicBlock ? 1 : blockText.trim() ? blockText.trimEnd().length : 0;
      const domNode = container.querySelector(`[data-offset-key^="${key}-"]`) as HTMLElement;

      if (!domNode) {
        charCount += blockCharCount;
        continue;
      }

      if (charCount + blockCharCount < freeChars) {
        charCount += blockCharCount;
        continue;
      }

      const charsInThisBlock = freeChars - charCount;

      if (isAtomicBlock) {
        const imgEl = domNode.querySelector("img");
        if (imgEl && imageBlurPercentRef.current > 0) {
          const imgRect = imgEl.getBoundingClientRect();
          return imgRect.top - containerRect.top + imgRect.height * imageBlurPercentRef.current;
        }
        const nodeRect = domNode.getBoundingClientRect();
        return charsInThisBlock >= blockCharCount
          ? nodeRect.bottom - containerRect.top
          : nodeRect.top - containerRect.top;
      }

      if (charsInThisBlock <= 0) {
        const nodeRect = domNode.getBoundingClientRect();
        return nodeRect.top - containerRect.top;
      }

      const textNodes = getTextNodesInElement(domNode);
      let currentOffset = 0;

      for (const textNode of textNodes) {
        const text = textNode.textContent || "";

        if (!text.length) continue;

        if (currentOffset + text.length >= charsInThisBlock) {
          const localIndex = charsInThisBlock - currentOffset;
          const range = document.createRange();

          const lastFreeIndex = Math.min(Math.max(localIndex, 1), text.length) - 1;
          range.setStart(textNode, lastFreeIndex);
          range.setEnd(textNode, lastFreeIndex + 1);

          const rect = range.getBoundingClientRect();
          return rect.bottom - containerRect.top;
        }

        currentOffset += text.length;
      }

      const nodeRect = domNode.getBoundingClientRect();
      return nodeRect.bottom - containerRect.top;
    }

    return container.scrollHeight;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const startY = e.clientY;
    const container = editorContainerRef.current;

    if (!container) return;

    const startLineY = lineY;
    const containerHeight = container.scrollHeight;

    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";

    let rafId: number | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        const deltaY = e.clientY - startY;
        let newLineY = startLineY + deltaY;

        const minY = 0;
        const maxY = containerHeight - 20;

        newLineY = Math.max(minY, Math.min(newLineY, maxY));

        setLineY(newLineY);

        const freeChars = convertLineYToFreeCharCountOptimized(newLineY + LINE_VISUAL_OFFSET);
        setFreeCharCount(freeChars);

        rafId = null;
      });
    };

    const handleMouseUp = () => {
      document.body.style.userSelect = "";
      document.body.style.webkitUserSelect = "";
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const findUncoveredRanges = (
  //   start: number,
  //   end: number,
  //   coveredRanges: { start: number; end: number }[]
  // ): { start: number; end: number }[] => {
  //   if (coveredRanges.length === 0) {
  //     return [{ start, end }];
  //   }

  //   const sorted = [...coveredRanges].sort((a, b) => a.start - b.start);

  //   const merged: { start: number; end: number }[] = [];
  //   sorted.forEach((range) => {
  //     if (merged.length === 0) {
  //       merged.push({ ...range });
  //     } else {
  //       const last = merged[merged.length - 1];
  //       if (range.start <= last.end) {
  //         last.end = Math.max(last.end, range.end);
  //       } else {
  //         merged.push({ ...range });
  //       }
  //     }
  //   });

  //   const uncovered: { start: number; end: number }[] = [];
  //   let currentPos = start;

  //   merged.forEach((range) => {
  //     if (currentPos < range.start) {
  //       uncovered.push({ start: currentPos, end: range.start });
  //     }
  //     currentPos = Math.max(currentPos, range.end);
  //   });

  //   if (currentPos < end) {
  //     uncovered.push({ start: currentPos, end });
  //   }

  //   return uncovered;
  // };

  const generateHtml = (editorState: EditorState): string => {
    const rawContent = convertToRaw(editorState.getCurrentContent());

    rawContent.blocks.forEach((block) => {
      block.inlineStyleRanges.forEach((range) => {
        if (range.style.startsWith("FONTSIZE-")) {
          range.style = range.style.replace("FONTSIZE-", "fontsize-") as typeof range.style;
        }
      });
    });

    const html = draftToHtml(rawContent, undefined, undefined, (entity) => {
      if (entity.type === "IMAGE") {
        const { src } = entity.data;
        return `<img src="${src}" />`;
      }
    });

    return html.replace(/<p><\/p>/g, "<br>");
  };

  const summaryHtml = generateHtml(editorStateSummary);
  const bodyHtml = generateHtml(editorStateBody);
  const previewContent = summaryHtml + "<br>" + bodyHtml;
  const html = summaryHtml + "<br>" + bodyHtml;

  const savedHtml = useMemo(() => {
    if (!isPreview || !editPost?.content) return null;
    const afterH2 = editPost.content.replace(/<h2[^>]*>[\s\S]*?<\/h2>/i, "");
    const sepMatch = afterH2.match(/<separator>([\s\S]*?)<\/separator>/i);
    return {
      summary: sepMatch?.[1] ?? "",
      body: afterH2.replace(/<separator>[\s\S]*?<\/separator>/i, "").replace(/^<br\s*\/?>/, ""),
    };
  }, [isPreview, editPost?.content]);

  const customHTMLToContentState = (htmlString: string): ContentState => {
    const preProcessed = htmlString.replace(/<ins>/g, "<u>").replace(/<\/ins>/g, "</u>");

    // Convert top-level <br> tags back to <p> blocks for Draft.js.
    // Uses zero-width space so convertFromHTML doesn't strip empty blocks.
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = preProcessed;
    Array.from(tempContainer.childNodes).forEach((child) => {
      if (child.nodeName === "BR") {
        const p = document.createElement("p");
        p.textContent = "\u200B";
        tempContainer.replaceChild(p, child);
      }
    });
    const processedHtml = tempContainer.innerHTML;

    const extractedImages: string[] = [];
    const htmlWithPlaceholders = processedHtml.replace(
      /<p>\s*<img\s[^>]*?src="([^"]+)"[^>]*?\/?>\s*<\/p>|<img\s[^>]*?src="([^"]+)"[^>]*?\/?>/gi,
      (_match, src1, src2) => {
        const src = src1 || src2;
        const idx = extractedImages.length;
        extractedImages.push(src);
        return `<p>__IMG_PLACEHOLDER_${idx}__</p>`;
      }
    );

    const { contentBlocks, entityMap } = convertFromHTML(htmlWithPlaceholders);

    let contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlWithPlaceholders;

    const domBlocks = Array.from(tempDiv.children) as HTMLElement[];
    const blocks = contentState.getBlocksAsArray();

    const updatedBlocks = blocks.map((block, index) => {
      const domNode = domBlocks[index];
      if (!domNode) return block;

      let data = block.getData();

      if (domNode.style?.textAlign) {
        data = data.set("text-align", domNode.style.textAlign);
      }

      if (domNode.style?.fontSize) {
        data = data.set("font-size", domNode.style.fontSize.replace(/px|;/g, ""));
      }

      return block.merge({ data }) as ContentBlock;
    });

    contentState = ContentState.createFromBlockArray(updatedBlocks, entityMap);

    const applyInlineFontSizes = (root: HTMLElement, block: ContentBlock) => {
      let offset = 0;
      const blockTextLength = block.getText().length;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);

      while (walker.nextNode()) {
        const textNode = walker.currentNode as Text;

        const textLength = textNode.textContent?.length ?? 0;
        if (!textLength) continue;

        const start = offset;
        const end = offset + textLength;

        if (start >= blockTextLength) break;
        if (end > blockTextLength) {
          offset += textLength;
          continue;
        }

        let el = textNode.parentElement;
        while (el && el !== root) {
          if (el.style?.fontSize) {
            const size = el.style.fontSize.replace("px", "");

            const selection = SelectionState.createEmpty(block.getKey()).merge({
              anchorOffset: start,
              focusOffset: end,
            }) as SelectionState;

            contentState = Modifier.applyInlineStyle(contentState, selection, `FONTSIZE-${size}`);
            break;
          }
          el = el.parentElement;
        }

        offset += textLength;
      }
    };

    domBlocks.forEach((domBlock, index) => {
      const block = contentState.getBlocksAsArray()[index];
      if (!block) return;

      applyInlineFontSizes(domBlock, block);
    });

    if (extractedImages.length > 0) {
      const finalBlocks = contentState.getBlocksAsArray();
      const newBlocks: ContentBlock[] = [];

      finalBlocks.forEach((block) => {
        const text = block.getText().trim();
        const match = text.match(/^__IMG_PLACEHOLDER_(\d+)__$/);
        if (match) {
          const imgIndex = parseInt(match[1], 10);
          const src = extractedImages[imgIndex];
          if (src) {
            contentState = contentState.createEntity("IMAGE", "IMMUTABLE", {
              src,
            });
            const entityKey = contentState.getLastCreatedEntityKey();
            const charData = CharacterMetadata.create({ entity: entityKey });
            const atomicBlock = new ContentBlock({
              key: genKey(),
              type: "atomic",
              text: " ",
              characterList: List([charData]),
            });
            newBlocks.push(atomicBlock);
          } else {
            newBlocks.push(block);
          }
        } else {
          newBlocks.push(block);
        }
      });

      contentState = ContentState.createFromBlockArray(newBlocks, contentState.getEntityMap());
    }

    // Clean up zero-width spaces used to preserve empty blocks through convertFromHTML
    const allBlocks = contentState.getBlocksAsArray();
    const cleanedBlocks = allBlocks.map((block) => {
      if (block.getText() === "\u200B") {
        return block.merge({ text: "", characterList: List() }) as ContentBlock;
      }
      return block;
    });
    contentState = ContentState.createFromBlockArray(cleanedBlocks, contentState.getEntityMap());

    return contentState;
  };

  const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPEG and PNG are allowed.");
      return;
    }
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    if (editPost?.attachments?.[0]?.url) {
      const oldUrl = editPost.attachments[0].url;

      setRemovedImageUrls((prev) => prev.filter((url) => url !== oldUrl));

      // Replace previous cover replacement if any (avoid duplicates)
      setReplacementFiles((prev) => {
        const idx = imageReplacements.indexOf(oldUrl);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = file;
          return updated;
        }
        return [...prev, file];
      });
      setImageReplacements((prev) => {
        if (prev.includes(oldUrl)) return prev;
        return [...prev, oldUrl];
      });
    } else {
      setImageFile(file);
    }

    setImagePreview(URL.createObjectURL(file));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleKeyCommand = (command: string, state: EditorState) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      setCurrentEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  const toggleInlineStyle = (style: string) => {
    const newState = RichUtils.toggleInlineStyle(currentEditorState, style);
    setCurrentEditorState(newState);
  };

  const toggleBlockData = (key: string, value: string) => {
    const selection = currentEditorState.getSelection();
    const contentState = currentEditorState.getCurrentContent();
    const blockKey = selection.getStartKey();
    const block = contentState.getBlockForKey(blockKey);
    const blockData = block.getData().merge({ [key]: value });

    const newContentState = contentState.merge({
      // @ts-ignore
      blockMap: contentState.getBlockMap().set(blockKey, block.merge({ data: blockData })),
    }) as ContentState;

    const newState = EditorState.push(currentEditorState, newContentState, "change-block-data");

    setCurrentEditorState(newState);
  };

  const handleUndo = () => {
    setCurrentEditorState(EditorState.undo(currentEditorState));
  };

  const getBlockStyle = (block: ContentBlock) => {
    const data = block.getData();
    const align = data.get("text-align");
    const fontSize = data.get("font-size");

    const classes = [];
    if (align) classes.push(`align-${align}`);
    if (fontSize) classes.push(`font-size-${fontSize}`);
    return classes.join(" ");
  };

  const extractUrlsFromHtml = (html: string): string[] => {
    const matches = html.match(/<img[^>]+src="([^">]+)"/g) || [];

    return matches.map((tag) => tag.match(/src="([^"]+)"/)?.[1]).filter(Boolean) as string[];
  };
  const handleRemoveImage = () => {
    if (imagePreview && editPost?.attachments?.[0]?.url) {
      const oldUrl = editPost.attachments[0].url;

      if (!imageReplacements.includes(oldUrl)) {
        setRemovedImageUrls((prev) => [...prev, oldUrl]);
      }
    }

    setImagePreview(null);
    setImageFile(null);
  };

  const applyFontSizeAndBlockData = (fontSize: string, alignment?: string) => {
    const selection = currentEditorState.getSelection();
    let contentState = currentEditorState.getCurrentContent();

    if (alignment) {
      const blockKey = selection.getStartKey();
      const block = contentState.getBlockForKey(blockKey);
      const blockData = block.getData().merge({ "text-align": alignment });

      contentState = contentState.merge({
        // @ts-ignore
        blockMap: contentState.getBlockMap().set(blockKey, block.merge({ data: blockData })),
      }) as ContentState;
    }

    Object.keys(fontSizeStyles).forEach((style) => {
      contentState = Modifier.removeInlineStyle(contentState, selection, style);
    });

    const contentWithFontSize = Modifier.applyInlineStyle(
      contentState,
      selection,
      `FONTSIZE-${fontSize}`
    );

    const newEditorState = EditorState.push(
      currentEditorState,
      contentWithFontSize,
      "change-inline-style"
    );

    setCurrentEditorState(newEditorState);
  };

  const applyHeading = (level: "h1" | "h2" | "paragraph") => {
    const config = {
      h1: { size: "32", bold: true },
      h2: { size: "24", bold: true },
      paragraph: { size: "16", bold: false },
    }[level];

    const selection = currentEditorState.getSelection();
    const content = currentEditorState.getCurrentContent();

    const targetSelection = selection.isCollapsed()
      ? (selection.merge({
          anchorKey: selection.getStartKey(),
          anchorOffset: 0,
          focusKey: selection.getStartKey(),
          focusOffset: content.getBlockForKey(selection.getStartKey()).getLength(),
          isBackward: false,
        }) as SelectionState)
      : selection;

    let newContent = content;
    Object.keys(fontSizeStyles).forEach((style) => {
      newContent = Modifier.removeInlineStyle(newContent, targetSelection, style);
    });
    newContent = Modifier.applyInlineStyle(newContent, targetSelection, `FONTSIZE-${config.size}`);
    newContent = config.bold
      ? Modifier.applyInlineStyle(newContent, targetSelection, "BOLD")
      : Modifier.removeInlineStyle(newContent, targetSelection, "BOLD");

    const newEditorState = EditorState.push(currentEditorState, newContent, "change-inline-style");
    setCurrentEditorState(EditorState.forceSelection(newEditorState, selection));
    setSelectedSize(config.size);
  };

  const fileInputEditorRef = useRef<HTMLInputElement>(null);

  const insertImage = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const placeholderContent = currentEditorState
      .getCurrentContent()
      .createEntity("IMAGE", "IMMUTABLE", { loading: true });
    const entityKey = placeholderContent.getLastCreatedEntityKey();

    const stateWithPlaceholder = AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(currentEditorState, { currentContent: placeholderContent }),
      entityKey,
      " "
    );
    setCurrentEditorState(stateWithPlaceholder);

    try {
      const res = await uploadInlineImage(formData).unwrap();

      setCurrentEditorState((prev) => {
        const content = prev.getCurrentContent();

        content.replaceEntityData(entityKey, { src: res.url, loading: false });

        const newContent = ContentState.createFromBlockArray(
          content.getBlocksAsArray(),
          content.getEntityMap()
        );
        return EditorState.forceSelection(
          EditorState.push(prev, newContent, "apply-entity"),
          prev.getSelection()
        );
      });
    } catch (error) {
      console.error("Failed to upload image", error);
      setCurrentEditorState((prev) => {
        const content = prev.getCurrentContent();
        const blockMap = content.getBlockMap().filter((block) => {
          if (block!.getType() !== "atomic") return true;
          return block!.getEntityAt(0) !== entityKey;
        });
        const newContent = content.merge({ blockMap }) as ContentState;
        return EditorState.push(prev, newContent, "remove-range");
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      insertImage(file);
      e.target.value = "";
    }
  };

  const handleBodyPastedFiles = (files: Blob[]): DraftHandleValue => {
    const imageFile = files.find((file) => file.type.startsWith("image/"));
    if (!imageFile) return "not-handled";
    insertImage(imageFile as File);
    return "handled";
  };

  const handleRedo = () => setCurrentEditorState(EditorState.redo(currentEditorState));

  const handleChipClick = (label: string) => {
    setChipSelect((prev) => {
      if (prev.includes(label)) {
        return prev.filter((item) => item !== label);
      } else {
        return [...prev, label];
      }
    });
  };

  useEffect(() => {
    if (!editPost || !editPost.freeContentRange) return;

    const raw = Number(editPost.freeContentRange);
    const freeChars = Math.floor(raw);
    setFreeCharCount(freeChars);
    imageBlurPercentRef.current = Math.round((raw - freeChars) * 100) / 100;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;

    const trySetLineY = () => {
      if (cancelled) return;

      const container = editorContainerRef.current;
      if (!container) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(trySetLineY, 100);
        }
        return;
      }

      const contentState = editorStateBodyRef.current.getCurrentContent();
      const plainText = contentState.getPlainText();

      if (!plainText.trim()) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(trySetLineY, 200);
        }
        return;
      }

      const blocks = contentState.getBlocksAsArray();
      const firstBlock = blocks[0];

      if (!firstBlock || !container.querySelector(`[data-offset-key^="${firstBlock.getKey()}-"]`)) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(trySetLineY, 100);
        }
        return;
      }
      const newLineY = convertFreeCharCountToLineY(freeChars);

      if (newLineY <= 0 && freeChars > 0) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(trySetLineY, 200);
        }
        return;
      }

      console.log(
        `[paidLine] postUid=${editPost?.uid ?? editPost?.id} savedFreeRange=${raw} -> lineY=${Math.round(newLineY - LINE_VISUAL_OFFSET)}`
      );

      setLineY(Math.max(0, newLineY - LINE_VISUAL_OFFSET));
      setLineYReady(true);
    };

    setTimeout(trySetLineY, 300);

    return () => {
      cancelled = true;
    };
  }, [editPost]);

  function Clear() {
    setPreview(false);
    setImageFile(null);
    setImagePreview(null);
    setEditorStateSummary(EditorState.createEmpty(decorator));
    setEditorStateTitle(EditorState.createEmpty(decorator));
    setEditorStateBody(EditorState.createEmpty(decorator));
    setIsPaidContent(false);
    setPrice("");
    setSlider(0);
    setTitle("");
    setChipSelect([]);
    setFreeCharCount(0);
    setLineY(0);
    setIsPublished(false);
    setReplacementFiles([]);
    setImageReplacements([]);
    setRemovedImageUrls([]);
    setSearchTerm("");
    setMentionStartPos(null);
  }

  const handleCheck = async (tickers: string[]): Promise<string[]> => {
    const invalidTickers: string[] = [];
    await Promise.all(
      tickers.map(async (ticker) => {
        try {
          const res = await validateSymbol({
            symbol: ticker.slice(1).toUpperCase(),
          }).unwrap();
          if (!res.valid) {
            invalidTickers.push(ticker);
          }
        } catch (e) {
          console.error(`API error for ${ticker}:`, e);
          invalidTickers.push(ticker);
        }
      })
    );

    return invalidTickers;
  };

  const handlePublish = async (published: boolean) => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    if (!published) {
      dispatch(setDraftLoading({ loading: true, type: "articles" }));
    }
    try {
      return await publishPost(published);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      if (!published) {
        dispatch(setDraftLoading({ loading: false, type: null }));
      }
    }
  };

  const publishPost = async (published: boolean) => {
    const htmlTitle = generateHtml(editorStateTitle);
    const htmlSummary = generateHtml(editorStateSummary);
    const htmlBody = generateHtml(editorStateBody);
    const summaryText = editorStateSummary.getCurrentContent().getPlainText().trim();
    const bodyText = editorStateBody.getCurrentContent().getPlainText().trim();
    const titleTrimmed = editorStateTitle.getCurrentContent().getPlainText().trim();

    if (!titleTrimmed && !summaryText && !bodyText && !imageFile) {
      return false;
    }

    if (isPaidContent && Number(price) <= 0) {
      setInvalidPrice(true);
      return false;
    }
    setInvalidPrice(false);

    const urls = html.match(/https?:\/\/[^\s"]+/g) || [];
    const cleanUrls = urls
      .map((url) => url.replace(/<\/?[^>]+(>|$)/g, ""))
      .filter((url) => !/\.(png|jpg|jpeg|gif|webp|svg)(\?.*)?$/i.test(url))
      .filter((url) => !url.includes("/inline-images/"))
      .filter((url) => !url.includes("/posts/"));
    const contentState = currentEditorState.getCurrentContent().getPlainText();
    const tickers = contentState.match(/\$[a-zA-Z]+/g) || [];
    const invalidTickers = await handleCheck(tickers);

    if (invalidTickers.length > 0) {
      setInvalidCashtags(invalidTickers);
      return false;
    } else {
      setInvalidCashtags([]);
    }

    const formData = new FormData();
    if (imageFile) {
      safeAppend(formData, "files", imageFile);
    }
    safeAppend(
      formData,
      "content",
      `<h2>${htmlTitle}</h2><separator>${htmlSummary}</separator><br>${htmlBody}`
    );
    if (!editPost) {
      safeAppend(formData, "isLongForm", true.toString());
    }
    safeAppend(formData, "postType", isPaidContent ? "PAID" : "FREE");

    safeAppend(formData, "topic", "STOCKS");
    safeAppend(formData, "sentiment", slider + 5);
    safeAppend(formData, "isPublished", published.toString());

    cleanUrls.forEach((url) => formData.append("sources", url));
    if (isPaidContent) {
      safeAppend(formData, "isArbitraryPrice", allowAnyAmount);
      safeAppend(formData, "price", price);
      const blurPercent = imageBlurPercentRef.current;
      const freeRange =
        blurPercent > 0 && blurPercent < 1
          ? freeCharCount + Math.round(blurPercent * 100) / 100
          : freeCharCount;
      safeAppend(formData, "freeContentRange", freeRange.toString());
    }

    const formatted = chipSelect.map((tag) => tag.replace(/^#/, "").toUpperCase());
    safeAppend(formData, "subtypes", formatted);

    const isPublishingDraft =
      published &&
      !!editPost &&
      (isDraft || editPost.published === false || editPost.isPublished === false);

    try {
      if (published) {
        dispatch(resetPage());

        if (isPublishingDraft) {
          dispatch(setDraftLoading({ loading: true, type: "articles" }));
        } else if (editPost) {
          dispatch(
            setEditingPostLoading({
              loading: true,
              postId: editPost.uid,
            })
          );
        } else {
          dispatch(setPostLoading(true));
        }
      } else {
        dispatch(setDraftLoading({ loading: true, type: "articles" }));
      }
      handleClose();

      let resultUid: string | undefined;

      if (editPost) {
        const oldInlineUrls = extractUrlsFromHtml(editPost.content);
        const newInlineUrls = extractUrlsFromHtml(htmlBody);

        const removedInline = oldInlineUrls.filter((url) => !newInlineUrls.includes(url));

        const finalRemoved = removedInline.filter((url) => !imageReplacements.includes(url));

        const allRemoved = [...removedImageUrls, ...finalRemoved];

        const conflict = imageReplacements.some((url) => allRemoved.includes(url));

        if (conflict) {
          setToast({
            message: "Cannot replace and remove the same image in one request.",
            success: false,
          });
          return false;
        }

        replacementFiles.forEach((file) => {
          formData.append("files", file);
        });

        imageReplacements.forEach((url) => {
          formData.append("imageReplacements", url);
        });

        allRemoved.forEach((url) => {
          formData.append("removedImageUrls", url);
        });

        const response = await updatePost({
          uid: editPost.uid,
          formData,
        }).unwrap();

        resultUid = response.uid;
      } else {
        const response = await createLongPost(formData).unwrap();
        resultUid = response.uid;
      }

      setPreview(false);

      setTimeout(() => {
        setToast({
          message: published
            ? "Article successfully published."
            : "Your article has been saved in drafts.",
          success: true,
          link: published
            ? { text: "View", href: `/post/${resultUid}` }
            : { text: "View drafts", href: "/drafts?type=Articles" },
        });
      }, 3000);

      return true;
    } catch (e) {
      if (!editPost && published) {
        try {
          formData.set("isPublished", false.toString());
          await createLongPost(formData).unwrap();

          setPreview(false);

          setTimeout(() => {
            setToast({
              message: "Couldn't publish your article, so it was saved in drafts.",
              success: false,
              link: { text: "View drafts", href: "/drafts?type=Articles" },
            });
          }, 3000);

          return true;
        } catch (draftError) {
          setToast({ message: "Failed to publish post", success: false });
          return false;
        }
      }

      setToast({ message: "Failed to publish post", success: false });
      return false;
    } finally {
      if (published) {
        if (isPublishingDraft) {
          dispatch(setDraftLoading({ loading: false, type: null }));
        } else if (editPost) {
          dispatch(
            setEditingPostLoading({
              loading: false,
              postId: null,
            })
          );
        } else {
          dispatch(setPostLoading(false));
        }
      } else {
        dispatch(
          setDraftLoading({
            loading: false,
            type: null,
          })
        );
      }
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const { data: searchCompanies, isFetching: isSearchFetching } = useGetUniversalSearchQuery(
    {
      limit: 5,
      query: searchTerm,
      type: "all",
    },
    {
      skip: !searchTerm,
    }
  );

  const autocompleteResults = (searchCompanies?.results ?? []).map((c: any) => ({
    data: {
      name: c.data.name,
      code: c.data.code,
      type: c.data.type,
    },
  }));

  const handleEditorChange = (state: EditorState, type: "title" | "summary" | "body") => {
    if (type === "title") setEditorStateTitle(state);
    else if (type === "summary") setEditorStateSummary(state);
    else if (type === "body") setEditorStateBody(state);

    const content = state.getCurrentContent();
    const selection = state.getSelection();
    const block = content.getBlockForKey(selection.getStartKey());
    const textBefore = block.getText().slice(0, selection.getStartOffset());

    const match = textBefore.match(/\$(\w*)$/);
    if (match) {
      setSearchTerm(match[1]);
      setHighlightedIndex(0);

      if (!mentionStartPos) {
        requestAnimationFrame(() => {
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;

          const range = sel.getRangeAt(0).cloneRange();
          const rect = range.getBoundingClientRect();
          const containerRect = formContainerRef.current?.getBoundingClientRect();
          if (!containerRect) return;

          const pos = {
            top: rect.bottom - containerRect.top + 8,
            left: rect.left - containerRect.left,
          };

          setMentionStartPos(pos);
          setDropdownPos(pos);

          const scrollEl = scrollContainerRef.current;
          if (scrollEl) {
            const scrollRect = scrollEl.getBoundingClientRect();
            const dropdownHeight = 256;
            const dropdownBottomViewport = rect.bottom + 8 + dropdownHeight;
            if (dropdownBottomViewport > scrollRect.bottom) {
              scrollEl.scrollTop += dropdownBottomViewport - scrollRect.bottom + 16;
            }
          }
        });
      }
    } else {
      setSearchTerm("");
      setMentionStartPos(null);
    }
  };

  const insertMention = (mention: { data: { code: string } }) => {
    const content = currentEditorState.getCurrentContent();
    const selection = currentEditorState.getSelection();
    const block = content.getBlockForKey(selection.getStartKey());
    const textBefore = block.getText().slice(0, selection.getStartOffset());

    const match = textBefore.match(/\$(\w*)$/);
    if (!match) return;

    const start = selection.getStartOffset() - match[0].length;
    const end = selection.getStartOffset();

    const newSelection = selection.merge({
      anchorOffset: start,
      focusOffset: end,
    }) as SelectionState;

    const newContent = Modifier.replaceText(content, newSelection, `$${mention.data.code} `);
    const withMention = EditorState.push(currentEditorState, newContent, "insert-characters");

    const mentionLength = `$${mention.data.code} `.length;
    const updatedSelection = SelectionState.createEmpty(selection.getStartKey()).merge({
      anchorOffset: start + mentionLength,
      focusOffset: start + mentionLength,
    });

    const finalEditorState = EditorState.forceSelection(
      withMention,
      updatedSelection as SelectionState
    );

    setCurrentEditorState(finalEditorState);
    setSearchTerm("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!searchTerm || autocompleteResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % autocompleteResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev === 0 ? autocompleteResults.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      insertMention(autocompleteResults[highlightedIndex]);
    } else if (e.key === "Escape") {
      setSearchTerm("");
    }
  };

  useEffect(() => {
    const titleText = editorStateTitle.getCurrentContent().getPlainText();
    const summaryText = editorStateSummary.getCurrentContent().getPlainText();
    const bodyText = editorStateBody.getCurrentContent().getPlainText();
    const allText = titleText + " " + summaryText + " " + bodyText;
    const cashtags = allText.match(TICKER_REGEX) || [];

    if (cashtags.length === 0) {
      setInvalidCashtags([]);
      return;
    }

    const handler = setTimeout(async () => {
      const invalids = await handleCheck(cashtags);
      setInvalidCashtags(invalids);
    }, 400);

    return () => clearTimeout(handler);
  }, [editorStateTitle, editorStateSummary, editorStateBody]);

  const subscriptionStatus = useSelector((state: RootState) => state.auth.subscriptionStatus);
  const isPremium = subscriptionStatus === "ACTIVE";
  const maxTotalChars = isPremium ? 100000 : 15000;

  const validationState = useMemo(() => {
    const titleLength = editorStateTitle.getCurrentContent().getPlainText("").trim().length;
    const summaryLength = editorStateSummary.getCurrentContent().getPlainText("").trim().length;
    const summaryCashtags =
      editorStateSummary.getCurrentContent().getPlainText(" ").match(TICKER_REGEX) || [];

    return {
      hasTitle: titleLength > 0,
      hasSummary:
        summaryLength > 0 &&
        summaryLength <= 500 &&
        summaryCashtags.length > 0 &&
        !summaryCashtags.some((tag) => invalidCashtags.includes(tag)),
      hasSubtype: chipSelect.length > 0,
    };
  }, [editorStateTitle, editorStateSummary, invalidCashtags, chipSelect]);

  const checkDisabled = useCallback(() => {
    const summaryLength = editorStateSummary.getCurrentContent().getPlainText("").length;
    const bodyLength = editorStateBody.getCurrentContent().getPlainText("").length;
    const disabled =
      !validationState.hasTitle ||
      !validationState.hasSummary ||
      !validationState.hasSubtype ||
      bodyLength > maxTotalChars ||
      summaryLength > 500 ||
      invalidCashtags.length > 0;
    return disabled;
  }, [validationState, editorStateSummary, editorStateBody, invalidCashtags, maxTotalChars]);

  const getCurrentFontSize = (state: EditorState, field: "title" | "summary" | "body"): string => {
    const inlineStyles = state.getCurrentInlineStyle();

    const fontSizeStyle = inlineStyles.toArray().find((style) => style.startsWith("FONTSIZE-"));

    return fontSizeStyle ? fontSizeStyle.replace("FONTSIZE-", "") : field === "title" ? "28" : "16";
  };

  useEffect(() => {
    const selection = currentEditorState.getSelection();
    if (!selection.getHasFocus()) return;

    const fontSize = getCurrentFontSize(currentEditorState, activeField);

    if (fontSize !== selectedSize) {
      setSelectedSize(fontSize);
    }
  }, [currentEditorState]);

  useEffect(() => {
    if (!editPost) return;
    setSlider(editPost.sentiment - 5);
    const tagsEdit = editPost.subtypes.map((tag: any) => "#" + tag.toLowerCase());
    setChipSelect(editPost.subtypes ? tagsEdit : []);

    const match = editPost.content.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i);
    const h2Text = match ? match[1] : null;
    setImagePreview(editPost.attachments?.[0]?.url ?? null);
    if (h2Text) {
      setTitle(h2Text);
      const titleContentState = customHTMLToContentState(h2Text);
      setEditorStateTitle(EditorState.createWithContent(titleContentState, decorator));
    }

    if (editPost.content) {
      const h2Match = editPost.content.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i);
      const h2Html = h2Match ? h2Match[1] : "";

      setTitle(h2Text);
      setEditorStateTitle(
        EditorState.createWithContent(customHTMLToContentState(h2Html), decorator)
      );

      const contentAfterH2 = editPost.content.replace(/<h2[^>]*>[\s\S]*?<\/h2>/i, "");

      const separatorMatch = contentAfterH2.match(/<separator>([\s\S]*?)<\/separator>/i);

      const safeSummaryHTML = separatorMatch?.[1] ?? "";

      const safeBodyHTML = contentAfterH2
        .replace(/<separator>[\s\S]*?<\/separator>/i, "")
        .replace(/^<br\s*\/?>/, "");

      if (safeSummaryHTML) {
        setEditorStateSummary(
          EditorState.createWithContent(customHTMLToContentState(safeSummaryHTML), decorator)
        );
      }
      if (safeBodyHTML) {
        setEditorStateBody(
          EditorState.createWithContent(customHTMLToContentState(safeBodyHTML), decorator)
        );
      }
    }

    if (typeof editPost.isPublished === "boolean") {
      setIsPublished(editPost.isPublished);
    }

    const isPaid = editPost.postType === "PAID" || Number(editPost.price) > 0;

    setIsPaidContent(isPaid);
    setTogglePaidContent(false);

    if (editPost.price) {
      setPrice(String(editPost.price));
    }

    if (editPost.freeContentRange) {
      const raw = Number(editPost.freeContentRange);
      setFreeCharCount(Math.floor(raw));
      imageBlurPercentRef.current = Math.round((raw - Math.floor(raw)) * 100) / 100;
    }

    const h2Match2 = editPost.content?.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i);
    const titleHtml2 = h2Match2 ? h2Match2[1] : "";
    const contentAfterH22 = (editPost.content || "").replace(/<h2[^>]*>[\s\S]*?<\/h2>/i, "");
    const sepMatch2 = contentAfterH22.match(/<separator>([\s\S]*?)<\/separator>/i);
    const summaryHtml2 = sepMatch2?.[1] ?? "";
    const bodyHtml2 = contentAfterH22
      .replace(/<separator>[\s\S]*?<\/separator>/i, "")
      .replace(/^<br\s*\/?>/, "");

    const initialTitleState = EditorState.createWithContent(
      customHTMLToContentState(titleHtml2),
      decorator
    );
    const initialSummaryState = summaryHtml2
      ? EditorState.createWithContent(customHTMLToContentState(summaryHtml2), decorator)
      : EditorState.createEmpty(decorator);
    const initialBodyState = bodyHtml2
      ? EditorState.createWithContent(customHTMLToContentState(bodyHtml2), decorator)
      : EditorState.createEmpty(decorator);

    setTimeout(() => {
      editSnapshotRef.current = {
        title: generateHtml(initialTitleState),
        summary: generateHtml(initialSummaryState),
        body: generateHtml(initialBodyState),
        image: editPost.attachments?.[0]?.url ?? null,
        chips: tagsEdit,
        slider: editPost.sentiment - 5,
        isPaid: isPaid,
        price: editPost.price ? String(editPost.price) : "",
        allowAny: false,
        freeCharCount: editPost.freeContentRange
          ? Math.floor(Number(editPost.freeContentRange))
          : 0,
      };
    }, 0);
  }, []);

  useEffect(() => {
    if (!editPost) return;

    const compare = () => {
      const snap = editSnapshotRef.current;
      if (!snap) return;
      const changed =
        generateHtml(editorStateTitle) !== snap.title ||
        generateHtml(editorStateSummary) !== snap.summary ||
        generateHtml(editorStateBody) !== snap.body ||
        imagePreview !== snap.image ||
        chipSelect.length !== snap.chips.length ||
        chipSelect.some((c, i) => c !== snap.chips[i]) ||
        slider !== snap.slider ||
        isPaidContent !== snap.isPaid ||
        price !== snap.price ||
        allowAnyAmount !== snap.allowAny ||
        freeCharCount !== snap.freeCharCount;
      setHasChanges(changed);
    };

    const timer = setTimeout(compare, 150);
    return () => clearTimeout(timer);
  }, [
    editorStateTitle,
    editorStateSummary,
    editorStateBody,
    imagePreview,
    chipSelect,
    slider,
    isPaidContent,
    price,
    allowAnyAmount,
    freeCharCount,
  ]);

  const titleHtmlRaw = generateHtml(editorStateTitle);
  const finalTitleHtml = titleHtmlRaw;

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      setHeaderScrolled(el.scrollTop > 0);
    };

    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollContainerRef]);

  return (
    <>
      <Modal
        open={open}
        onClose={closeAndDraft}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          padding: "2.5rem 3.375rem",
          overflow: "scroll",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "#ffffff90",
          },
        }}
      >
        <Box
          sx={{
            outline: "none",
            "&:focus": {
              outline: "none",
            },
            minHeight: 0,
            position: "relative",
            maxWidth: "83.25rem",
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
            background: "white",
            alignItems: "stretch",
            borderRadius: "16px",
            boxShadow: `
              0px 32px 48px -8px #0000001A,
              0px 0px 14px -4px #0000000D
            `,
          }}
        >
          {isPreview ? (
            <PreviewHeader onClose={handleClose} />
          ) : (
            <LongPostHeader
              handleClose={handleClose}
              handlePublish={handlePublish}
              setPreview={setPreview}
              checkDisabled={checkDisabled}
              preview={preview}
              isEdit={editPost}
              isPublished={isPublished}
              setIsPublished={setIsPublished}
              isPaidContent={isPaidContent}
              invalidPrice={invalidPrice}
              invalidCashtags={invalidCashtags}
              isSubmitting={isSubmitting}
              scrolled={headerScrolled}
              hasChanges={hasChanges}
              showHowToPublish={!showPublishRequirements}
              onShowPublishRequirements={() => setShowPublishRequirements(true)}
            />
          )}

          <Box
            ref={scrollContainerRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: "12px",
              marginRight: "12px",
            }}
            className="custom-scrollbar"
          >
            {preview || isPreview ? (
              <Box sx={{ width: "840px", padding: "20px" }}>
                <PreviewLongPost
                  summaryHtml={savedHtml ? savedHtml.summary : summaryHtml}
                  bodyHtml={savedHtml ? savedHtml.body : bodyHtml}
                  open={preview}
                  handleClose={handleClose}
                  imagePreview={imagePreview}
                  title={finalTitleHtml}
                  content={previewContent}
                  chips={chipSelect}
                  slider={slider}
                  isPaidContent={isPaidContent}
                  editPost={editPost}
                  price={price}
                  freeCharCount={freeCharCount}
                  imageBlurPercent={imageBlurPercentRef.current}
                  setToast={setToast}
                  isPreview={isPreview}
                  allowAnyAmount={allowAnyAmount}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  margin: "0 290px",
                  minHeight: "90vh",
                  height: "100%",
                  paddingBottom: showPublishRequirements ? "200px" : "80px",
                }}
              >
                <PostEditorForm
                  lineYReady={lineYReady}
                  formContainerRef={formContainerRef}
                  editorContainerRef={editorContainerRef}
                  title={title}
                  setTitle={setTitle}
                  imagePreview={imagePreview}
                  fileInputRef={fileInputRef}
                  handleSelectImage={handleSelectImage}
                  handleRemoveImage={handleRemoveImage}
                  editPost={editPost}
                  summaryEditorRef={summaryEditorRef}
                  bodyEditorRef={bodyEditorRef}
                  handleKeyDown={handleKeyDown}
                  editorStateSummary={editorStateSummary}
                  handleEditorChange={handleEditorChange}
                  handleKeyCommand={handleKeyCommand}
                  getBlockStyle={getBlockStyle}
                  searchTerm={searchTerm}
                  isSearchFetching={isSearchFetching}
                  autocompleteResults={autocompleteResults}
                  dropdownPos={dropdownPos}
                  highlightedIndex={highlightedIndex}
                  insertMention={insertMention}
                  slider={slider}
                  setSlider={setSlider}
                  chipSelect={chipSelect}
                  handleChipClick={handleChipClick}
                  editorStateBody={editorStateBody}
                  isPaidContent={isPaidContent}
                  setIsPaidContent={setIsPaidContent}
                  lineRef={lineRef}
                  handleMouseDown={handleMouseDown}
                  togglePaidContent={togglePaidContent}
                  setTogglePaidContent={setTogglePaidContent}
                  allowAnyAmount={allowAnyAmount}
                  setAllowAnyAmount={setAllowAnyAmount}
                  price={price}
                  setPrice={setPrice}
                  lineY={lineY}
                  handleUndo={handleUndo}
                  handleRedo={handleRedo}
                  selectedSize={selectedSize}
                  setSelectedSize={setSelectedSize}
                  applyFontSizeAndBlockData={applyFontSizeAndBlockData}
                  fontSizeOptions={fontSizeOptions}
                  toggleInlineStyle={toggleInlineStyle}
                  toggleBlockData={toggleBlockData}
                  fileInputEditorRef={fileInputEditorRef}
                  handleImageChange={handleImageChange}
                  handleBodyPastedFiles={handleBodyPastedFiles}
                  invalidPrice={invalidPrice}
                  setActiveField={(field: "title" | "summary" | "body") => {
                    if (field === "body") bodyWasFocusedRef.current = true;
                    setActiveField(field);
                  }}
                  editorStateTitle={editorStateTitle}
                  setInvalidPrice={setInvalidPrice}
                  maxTotalChars={maxTotalChars}
                />
              </Box>
            )}
          </Box>
          {!preview && !isPreview && (
            <Box
              sx={{
                position: "absolute",
                bottom: 22,
                left: "50%",
                transform: "translateX(-50%) translate(20px)",
                zIndex: 1300,
              }}
            >
              <EditorToolbar
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
                applyHeading={applyHeading}
                applyFontSizeAndBlockData={applyFontSizeAndBlockData}
                fontSizeOptions={fontSizeOptions}
                toggleInlineStyle={toggleInlineStyle}
                toggleBlockData={toggleBlockData}
                isPaidContent={isPaidContent}
                setIsPaidContent={setIsPaidContent}
                fileInputEditorRef={fileInputEditorRef}
                handleImageChange={handleImageChange}
                invalidPrice={invalidPrice}
              />
            </Box>
          )}
          {!preview && !isPreview && showPublishRequirements && (
            <Box
              sx={{
                position: "absolute",
                bottom: 22,
                left: "50%",
                transform: "translateX(-50%) translate(20px)",
                zIndex: 1301,
              }}
            >
              <ArticleInfoPanel
                validationState={validationState}
                onHide={() => setShowPublishRequirements(false)}
              />
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default CreateLongPostModal;
